import React, { memo, useState } from 'react';
import { SharedValue, useDerivedValue } from 'react-native-reanimated';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { Box } from '@/components/lib/components/Box';
import { AlternateProjectionRowExpandedContent } from '@/feature/player-profile-sheets/components/AlternateProjectionRowExpandedContent';
import { common } from '@/styles/styles';
import { League, MarketOption, ProjectionType, Sport } from '@/types/api.generated';
import { isProjectionSelected } from '@/utils/projections';

import { BetslipPick } from '../betslip-pickem/types';
import { AlternateProjectionRowContent } from './components/AlternateProjectionRowContent';

const getBorderRadius = (index: number, size: number) => {
    const isFirst = index === 0;
    const isLast = index === size - 1;

    return {
        borderTopLeftRadius: isFirst ? 16 : 0,
        borderTopRightRadius: isFirst ? 16 : 0,
        borderBottomLeftRadius: isLast ? 16 : 0,
        borderBottomRightRadius: isLast ? 16 : 0,
        borderTopWidth: 1,
        borderBottomWidth: isLast ? 1 : 0,
    };
};

const getInitialIndex = (projections: PlayerProjectionFragment[], playerPicked?: BetslipPick): number => {
    const selectedIndex = projections.findIndex(it => isProjectionSelected(it, playerPicked));
    if (selectedIndex !== -1) {
        return selectedIndex;
    }

    const regularIndex = projections.findIndex(it => it.type === ProjectionType.Regular);
    if (regularIndex !== -1) {
        return regularIndex;
    }

    // default to the first projection if no selected or regular found
    return 0;
};

export const AlternateProjectionValues = memo(
    ({
        projectionsGroup,
        index,
        expandedIndex,
        groupsCount,
        hideExpandButton,
        removePlayer,
        playerPicked,
        league,
        sport,
        selectMarketOption,
    }: {
        projectionsGroup: PlayerProjectionFragment[];
        index: number;
        expandedIndex: SharedValue<number>;
        groupsCount: number;
        hideExpandButton: boolean;
        league: League;
        sport: Sport;
        removePlayer: () => void;
        selectMarketOption: (projection: PlayerProjectionFragment, option: MarketOption) => void;
        playerPicked?: BetslipPick;
    }) => {
        const expanded = useDerivedValue(() => expandedIndex.value === index);
        const initialIndex = getInitialIndex(projectionsGroup, playerPicked);
        const [activeAlternateIndex, setActiveAlternateIndex] = useState(initialIndex);
        const borderRadius = getBorderRadius(index, groupsCount);
        const groupDisplayProjection = projectionsGroup[activeAlternateIndex];

        if (!groupDisplayProjection) {
            return null;
        }

        return (
            <Box
                backgroundColor={'neutral800'}
                style={{ ...borderRadius, ...common.borderCurve }}
                borderWidth={1}
                borderColor={'neutral700'}
            >
                <AlternateProjectionRowContent
                    rowIndex={index}
                    projectionsGroup={projectionsGroup}
                    activeAlternateIndex={activeAlternateIndex}
                    expanded={expanded}
                    expandedIndex={expandedIndex}
                    hideExpandButton={hideExpandButton}
                    selectMarketOption={selectMarketOption}
                    removePlayer={removePlayer}
                    playerPicked={playerPicked}
                    league={league}
                    sport={sport}
                />
                <AlternateProjectionRowExpandedContent
                    projectionsGroup={projectionsGroup}
                    rowIndex={index}
                    expanded={expanded}
                    expandedIndex={expandedIndex}
                    setActiveAlternateIndex={val => setActiveAlternateIndex(val)}
                    playerPicked={playerPicked}
                    activeAlternateIndex={activeAlternateIndex}
                />
            </Box>
        );
    }
);
